/**
 * Silo Easy Pay LP — bundle system.
 *
 * Three custom elements, one data flow:
 *   <ep-bundle-picker>    accordion + add-to-cart; broadcasts the selected
 *                         bundle as an `ep:bundle-change` document event.
 *   <ep-payment-summary>  mirrors the selection (extends EpBundleMirror).
 *   <ep-sticky-bar>       mobile sticky ATC bar (extends EpBundleMirror).
 *
 * Mirror targets declare [data-ep-slot] with a data-ep-pattern using
 * placeholders [mo] [name] [sub] [now] [off] [months]. The section may be
 * rendered more than once by the editor and the script may load twice
 * (two sections reference it) — all defines are guarded.
 */
(() => {
  'use strict';

  const EVENT = 'ep:bundle-change';

  /** Admin price-display mode (theme setting, rendered on the picker):
   *  'full' shows the one-time price wherever a pattern says [mo]. */
  const priceDisplayFull = () => {
    const el = document.querySelector('[data-ep-price-display]');
    return !!el && el.getAttribute('data-ep-price-display') === 'full';
  };

  /** Fill every [data-ep-slot] under root from its pattern + bundle data. */
  const fillSlots = (root, d) => {
    if (!d) return;
    const full = priceDisplayFull();
    // [was] = the pre-discount price, derived (now + off) so the event's
    // detail shape stays exactly as the admin systems know it.
    const cash = (t) => parseFloat(String(t).replace(/[^0-9.]/g, '')) || 0;
    const was = d.now && d.off ? '$' + Math.round(cash(d.now) + cash(d.off)) : '';
    root.querySelectorAll('[data-ep-slot]').forEach((el) => {
      let pattern = el.dataset.epPattern || '';
      // Mode-specific blocks author their tokens deliberately (a full-mode
      // line may SHOW the monthly figure) — the full remap only applies to
      // generic, mode-agnostic slots.
      const remap = full && !el.closest('[data-ep-mode-only]');
      // Full-payment remap: '[mo]/mo' collapses first so no stray '/mo'
      // suffix survives next to a one-time price.
      if (remap) pattern = pattern.replace('[mo]/mo', '[mo]');
      el.textContent = pattern
        .replace('[was]', was)
        .replace('[mo]', remap ? d.now : d.monthly)
        .replace('[name]', d.name)
        .replace('[subshort]', d.subshort) // before [sub] — it contains that token
        .replace('[sub]', d.sub)
        .replace('[now]', d.now)
        .replace('[off]', d.off)
        .replace('[months]', d.months);
    });
    // '/mo'-style suffix spans (payment summary, popup) follow the mode too.
    root.querySelectorAll('[data-ep-permo]').forEach((el) => {
      el.style.display = full ? 'none' : '';
    });
    // Mode-specific blocks (the hero renders one structure per mode).
    root.querySelectorAll('[data-ep-mode-only]').forEach((el) => {
      el.hidden = (el.dataset.epModeOnly === 'full') !== full;
    });
  };

  /** Ask the picker (if present) to re-broadcast its current selection. */
  let announceQueued = false;
  const requestAnnounce = () => {
    // coalesced: five mirrors booting = one broadcast, not five
    if (announceQueued) return;
    announceQueued = true;
    queueMicrotask(() => {
      announceQueued = false;
      const picker = document.querySelector('ep-bundle-picker');
      if (picker && typeof picker.announce === 'function') picker.announce();
    });
  };

  /**
   * Buy the currently selected bundle through the picker card's own CTA
   * (validation, error display, discount redirect). Mirrors the loading state
   * onto hostBtn and returns the watch interval id (0 if nothing to watch).
   */
  const buySelected = (hostBtn) => {
    const picker = document.querySelector('ep-bundle-picker');
    const cardCta = picker && picker.querySelector('[data-ep-card].is-open [data-ep-cta]');
    if (!cardCta) {
      const bundles = document.getElementById('ep-bundles');
      if (bundles) bundles.scrollIntoView({ behavior: 'smooth' });
      return 0;
    }
    if (hostBtn) { hostBtn.classList.add('is-loading'); hostBtn.setAttribute('aria-busy', 'true'); }
    cardCta.click();
    // If the add fails the page never navigates — release the host spinner
    // once the card button's own loading state clears.
    const watch = setInterval(() => {
      if (!cardCta.classList.contains('is-loading')) {
        clearInterval(watch);
        if (hostBtn) { hostBtn.classList.remove('is-loading'); hostBtn.removeAttribute('aria-busy'); }
      }
    }, 300);
    return watch;
  };

  if (!customElements.get('ep-bundle-picker')) {
    class EpBundlePicker extends HTMLElement {
      connectedCallback() {
        this._onClick = this._handleClick.bind(this);
        this.addEventListener('click', this._onClick);
        // Back/forward cache restores the page mid-"loading" after a checkout
        // redirect — reset the spinners or they stay stuck forever.
        this._onPageShow = () => this._resetLoading();
        window.addEventListener('pageshow', this._onPageShow);
        // A selection must always exist (mirrors depend on it) — if the admin
        // left no bundle preselected, open the first card.
        if (!this.querySelector('[data-ep-card].is-open')) {
          const first = this.querySelector('[data-ep-card]');
          if (first) {
            first.classList.add('is-open');
            const head = first.querySelector('[data-ep-head]');
            if (head) head.setAttribute('aria-expanded', 'true');
          }
        }
        this.announce();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        window.removeEventListener('pageshow', this._onPageShow);
      }

      _resetLoading() {
        this.querySelectorAll('[data-ep-cta].is-loading').forEach((btn) => {
          btn.classList.remove('is-loading');
          btn.removeAttribute('aria-busy');
        });
      }

      _handleClick(event) {
        const cta = event.target.closest('[data-ep-cta]');
        if (cta) {
          event.preventDefault();
          this._addToCart(cta);
          return;
        }
        const colorBtn = event.target.closest('[data-ep-color]');
        if (colorBtn) {
          this._setColor(colorBtn);
          return;
        }
        const priceBtn = event.target.closest('[data-ep-price-toggle]');
        if (priceBtn) {
          this._setPriceMode(priceBtn.dataset.epPriceToggle);
          return;
        }
        const head = event.target.closest('[data-ep-head]');
        if (head) this._open(head.closest('[data-ep-card]'));
      }

      /** Visitor price toggle: flips the picker's display mode, rewrites the
       *  server-rendered row prices from the cards' own data, and re-announces
       *  so every mirror (hero, payment, sticky, popup) and its /mo suffix
       *  follows through the normal fillSlots path. */
      _setPriceMode(mode) {
        if (mode !== 'monthly' && mode !== 'full') return;
        this.setAttribute('data-ep-price-display', mode);
        const scope = this.closest('#ep-bundles') || document;
        scope.querySelectorAll('[data-ep-price-toggle]').forEach((b) => {
          const on = b.dataset.epPriceToggle === mode;
          b.classList.toggle('is-selected', on);
          b.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        const months = this.dataset.months || '12';
        this.querySelectorAll('[data-ep-card]').forEach((card) => {
          const el = card.querySelector('[data-ep-row-price]');
          if (!el) return;
          const now = card.dataset.epNow || '';
          const mo = card.dataset.epMonthly || '';
          // Same two-line structure/classes the section renders server-side.
          el.innerHTML = mode === 'full'
            ? "<span class='ep-bundles__price-main ep-bundles__price-main--dark'></span>" +
              "<span class='ep-bundles__price-sub ep-bundles__price-sub--green'></span>"
            : "<span class='ep-bundles__price-main'><span class='ep-bundles__price-permo'>/mo</span></span>" +
              "<span class='ep-bundles__price-sub'></span>";
          const main = el.querySelector('.ep-bundles__price-main');
          const sub = el.querySelector('.ep-bundles__price-sub');
          if (mode === 'full') {
            main.textContent = now;
            sub.textContent = 'or ' + months + ' \u00d7 ' + mo + '/mo';
          } else {
            main.insertBefore(document.createTextNode(mo), main.firstChild);
            if (card.dataset.epWas) {
              sub.textContent = 'or ' + now + ' in full ';
              const was = document.createElement('s');
              was.textContent = card.dataset.epWas;
              sub.appendChild(was);
            } else {
              sub.textContent = 'or ' + now + ' total';
            }
          }
        });
        this.announce();
      }

      /** Color selector: every card's active variant flips to the chosen color
       *  (falls back to color 1 when a block has no color-2 variant). */
      _setColor(btn) {
        const second = btn.dataset.epColor === '2';
        this.dataset.epColorSel = second ? '2' : '1'; // CSS hook — swaps per-bundle images
        this.querySelectorAll('[data-ep-color]').forEach((b) => {
          const on = b === btn;
          b.classList.toggle('is-selected', on);
          b.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        this.querySelectorAll('[data-ep-card]').forEach((card) => {
          card.dataset.variantId = second && card.dataset.epVid2 ? card.dataset.epVid2 : card.dataset.epVid1 || card.dataset.variantId;
        });
        this.announce();
      }

      _open(card) {
        if (!card || card.classList.contains('is-open')) return;
        this.querySelectorAll('[data-ep-card]').forEach((el) => {
          const open = el === card;
          el.classList.toggle('is-open', open);
          const head = el.querySelector('[data-ep-head]');
          if (head) head.setAttribute(head.getAttribute('role') === 'radio' ? 'aria-checked' : 'aria-expanded', open ? 'true' : 'false');
        });
        this.announce(card);
      }

      /** Broadcast the selected bundle so mirror elements can follow it. */
      announce(card) {
        card = card || this.querySelector('[data-ep-card].is-open');
        if (!card) return;
        document.dispatchEvent(
          new CustomEvent(EVENT, {
            detail: {
              name: card.dataset.epName || '',
              sub: card.dataset.epSub || '',
              subshort: card.dataset.epSubshort || card.dataset.epSub || '',
              monthly: card.dataset.epMonthly || '',
              now: card.dataset.epNow || '',
              off: this.dataset.offTxt || '',
              months: this.dataset.months || '',
            },
          })
        );
      }

      async _addToCart(button) {
        if (button.classList.contains('is-loading')) return;
        const card = button.closest('[data-ep-card]');
        const variantId = Number(card && card.dataset.variantId);
        const error = card && card.querySelector('[data-ep-error]');
        if (error) error.hidden = true;
        if (!variantId) {
          this._fail(error, button);
          return;
        }
        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');
        try {
          // One bundle at a time: whatever is in the cart gets replaced.
          await fetch('/cart/clear.js', { method: 'POST' }).catch(() => {});
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            // _ep: hidden line property (underscore = not shown at checkout).
            // The silo-bundles app's staleness guard recognizes Silo system
            // lines by it — handle-agnostic, so duplicated LPs always pass.
            body: JSON.stringify({ id: variantId, quantity: 1, properties: { _ep: '1' } }),
          });
          if (!res.ok) throw new Error('add-failed ' + res.status);
          // Mark the cart so checkout can tell this order came from the LP —
          // unless this page's section opted out (Custom checkout toggle).
          // Fail-open: attribute absent = today's behavior (easy_pay set).
          // When off, easy_pay is actively CLEARED so a stale value from an
          // earlier easy-pay visit can't hijack the checkout.
          const epWrap = this.closest('[data-ep-custom-checkout]') || document.querySelector('[data-ep-custom-checkout]');
          const epCustom = !epWrap || epWrap.getAttribute('data-ep-custom-checkout') !== 'false';
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: { easy_pay: epCustom ? 'true' : '' } }),
          }).catch(() => {});
          // Grace window before navigating: the AJAX add publishes
          // product_added_to_cart to the web pixels (Meta/Google/etc.), but an
          // instant redirect unloads the page before their sandboxed iframes can
          // dispatch. ~1s lets AddToCart actually reach the networks.
          await new Promise((resolve) => setTimeout(resolve, 1000));
          window.location.assign(this._destination());
        } catch (_) {
          this._fail(error, button);
        }
      }

      _destination() {
        const target = this.dataset.redirect === 'checkout' ? '/checkout' : '/cart';
        const code = (this.dataset.discountCode || '').trim();
        if (code) return '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(target);
        return target;
      }

      _fail(error, button) {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        if (error) error.hidden = false;
      }
    }
    customElements.define('ep-bundle-picker', EpBundlePicker);
  }

  /** Shared base: subscribe to bundle changes, fill slots, clean up on disconnect. */
  class EpBundleMirror extends HTMLElement {
    connectedCallback() {
      this._onChange = (e) => this.renderData(e.detail);
      document.addEventListener(EVENT, this._onChange);
      requestAnnounce();
    }

    disconnectedCallback() {
      document.removeEventListener(EVENT, this._onChange);
    }

    renderData(d) {
      fillSlots(this, d);
      // the head's UTM hold ends with the first real fill (see theme.liquid / app-tailwind.css)
      document.documentElement.removeAttribute('data-ep-hold');
    }
  }

  if (!customElements.get('ep-payment-summary')) {
    class EpPaymentSummary extends EpBundleMirror {
      connectedCallback() {
        super.connectedCallback();
        this._onClick = (e) => {
          if (e.target.closest('[data-ep-payment-cta]')) this._buy();
        };
        this._onPageShow = () => {
          const btn = this.querySelector('[data-ep-payment-cta]');
          if (btn) btn.classList.remove('is-loading');
          if (this._watch) clearInterval(this._watch);
        };
        this.addEventListener('click', this._onClick);
        window.addEventListener('pageshow', this._onPageShow);
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('click', this._onClick);
        window.removeEventListener('pageshow', this._onPageShow);
        if (this._watch) clearInterval(this._watch);
      }

      _buy() {
        if (this._watch) clearInterval(this._watch);
        this._watch = buySelected(this.querySelector('[data-ep-payment-cta]'));
      }
    }
    customElements.define('ep-payment-summary', EpPaymentSummary);
  }

  if (!customElements.get('ep-sticky-bar')) {
    class EpStickyBar extends EpBundleMirror {
      connectedCallback() {
        super.connectedCallback();
        this._onClick = (e) => {
          if (e.target.closest('[data-ep-sticky-cta]')) this._buy();
        };
        this.addEventListener('click', this._onClick);
        // Reveal without a scroll listener: a 1px sentinel sits `data-reveal-after` px down the
        // document; once it has scrolled above the viewport the bar shows (compositor-driven).
        this._pastReveal = !('IntersectionObserver' in window);
        this._sentinel = document.createElement('div');
        this._sentinel.setAttribute('aria-hidden', 'true');
        this._sentinel.style.cssText = 'position:absolute;left:0;top:' + Number(this.dataset.revealAfter || 400) + 'px;width:1px;height:1px;pointer-events:none;';
        document.body.appendChild(this._sentinel);
        if ('IntersectionObserver' in window) {
          this._revealIO = new IntersectionObserver((entries) => {
            const en = entries[0];
            this._pastReveal = !en.isIntersecting && en.boundingClientRect.top < 0;
            this._toggle();
          });
          this._revealIO.observe(this._sentinel);
        }
        this._onPageShow = () => this._resetLoading();
        window.addEventListener('pageshow', this._onPageShow);
        // Give way to the footer: while it's on screen the bar slides away
        // so the footer is fully readable.
        this._footerInView = false;
        const footer = document.querySelector('.ep-footer');
        if (footer && 'IntersectionObserver' in window) {
          this._footerIO = new IntersectionObserver((entries) => {
            this._footerInView = entries[0].isIntersecting;
            this._toggle();
          });
          this._footerIO.observe(footer);
        }
        this.removeAttribute('hidden');
        this._toggle();
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('click', this._onClick);
        if (this._revealIO) this._revealIO.disconnect();
        if (this._sentinel) this._sentinel.remove();
        window.removeEventListener('pageshow', this._onPageShow);
        if (this._footerIO) this._footerIO.disconnect();
        if (this._watch) clearInterval(this._watch);
      }

      _resetLoading() {
        if (this._watch) {
          clearInterval(this._watch);
          this._watch = null;
        }
        const btn = this.querySelector('[data-ep-sticky-cta]');
        if (btn) btn.classList.remove('is-loading');
      }

      _toggle() {
        this.classList.toggle('is-visible', this._pastReveal && !this._footerInView);
      }

      _buy() {
        if (this._watch) clearInterval(this._watch);
        this._watch = buySelected(this.querySelector('[data-ep-sticky-cta]'));
      }
    }
    customElements.define('ep-sticky-bar', EpStickyBar);
  }

  /**
   * <ep-lp-popup> — LP discount modal. Opens after a delay (and/or on exit
   * intent), mirrors the selected bundle, CTA buys it via the picker. Closes
   * on X, backdrop, decline link and Escape; optionally once per session.
   */
  if (!customElements.get('ep-lp-popup')) {
    const SEEN_KEY = 'epLpPopupShown';

    const EMAIL_KEY = 'epEmailCaptured';

    class EpLpPopup extends EpBundleMirror {
      connectedCallback() {
        super.connectedCallback();
        this._opened = false;
        this._onClick = (e) => {
          if (e.target.closest('[data-ep-popup-cta]')) {
            // Email step gates the action once per visitor (when enabled).
            if (this.dataset.emailStep && !this._emailDone()) {
              this._showStep('email');
              return;
            }
            this._runAction(this.querySelector('[data-ep-popup-cta]'));
            return;
          }
          if (e.target.closest('[data-ep-email-back]')) {
            this._showStep('offer');
            return;
          }
          if (e.target.closest('[data-ep-popup-close]')) this.close();
        };
        this._onSubmit = (e) => {
          const form = e.target.closest('[data-ep-email-form]');
          if (!form) return;
          e.preventDefault();
          this._submitEmail(form);
        };
        this.addEventListener('submit', this._onSubmit);
        this._onKey = (e) => {
          if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
        };
        this._onExit = (e) => {
          if (e.clientY <= 0) this.open();
        };
        this._onPageShow = () => {
          const btn = this.querySelector('[data-ep-popup-cta]');
          if (btn) btn.classList.remove('is-loading');
          if (this._watch) clearInterval(this._watch);
        };
        this.addEventListener('click', this._onClick);
        document.addEventListener('keydown', this._onKey);
        window.addEventListener('pageshow', this._onPageShow);
        if (this.dataset.exitIntent) document.addEventListener('mouseleave', this._onExit);
        // The delay countdown starts at the FIRST interaction, not at load —
        // an auto-opening modal becomes the page's LCP element in lab runs
        // (and interrupts visitors who haven't engaged yet).
        this._interactEvents = ['pointerdown', 'scroll', 'keydown', 'touchstart'];
        this._onFirstInteract = () => {
          this._removeInteract();
          if (this._timer) return;
          const delay = Number(this.dataset.delay || 10);
          this._timer = setTimeout(() => this.open(), Math.max(0, delay) * 1000);
        };
        this._interactEvents.forEach((ev) =>
          window.addEventListener(ev, this._onFirstInteract, { passive: true, once: true })
        );
        this.removeAttribute('hidden');
      }

      _removeInteract() {
        if (!this._interactEvents) return;
        this._interactEvents.forEach((ev) => window.removeEventListener(ev, this._onFirstInteract));
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('click', this._onClick);
        this.removeEventListener('submit', this._onSubmit);
        document.removeEventListener('keydown', this._onKey);
        document.removeEventListener('mouseleave', this._onExit);
        window.removeEventListener('pageshow', this._onPageShow);
        this._removeInteract();
        if (this._timer) clearTimeout(this._timer);
        if (this._watch) clearInterval(this._watch);
      }

      _seen() {
        try {
          return this.dataset.once && sessionStorage.getItem(SEEN_KEY);
        } catch (_) {
          return false;
        }
      }

      open() {
        if (this._opened || this._seen()) return;
        this._opened = true;
        this.classList.add('is-open');
        try {
          if (this.dataset.once) sessionStorage.setItem(SEEN_KEY, '1');
        } catch (_) {}
        const x = this.querySelector('.ep-popup__x');
        if (x) x.focus();
      }

      close() {
        this.classList.remove('is-open');
      }

      _emailDone() {
        try { return localStorage.getItem(EMAIL_KEY) === '1'; } catch (_) { return false; }
      }

      _showStep(name) {
        this.querySelectorAll('[data-ep-step]').forEach((el) => {
          el.hidden = el.dataset.epStep !== name;
        });
        if (name === 'email') {
          const input = this.querySelector('.ep-popup__input');
          if (input) setTimeout(() => input.focus(), 50);
        }
      }

      /** Email capture: validate, hand the lead to the same contact-form
       *  pipeline the ROI popup posts to (fire-and-forget — the purchase is
       *  NEVER blocked on the email API), then continue into the buy flow. */
      _submitEmail(form) {
        const input = form.querySelector('.ep-popup__input');
        const error = this.querySelector('[data-ep-email-error]');
        const btn = form.querySelector('button[type="submit"]');
        const email = (input && input.value || '').trim();
        const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        if (!valid) {
          if (input) input.classList.add('is-invalid');
          if (error) error.hidden = false;
          return;
        }
        if (input) input.classList.remove('is-invalid');
        if (error) error.hidden = true;
        try { localStorage.setItem(EMAIL_KEY, '1'); } catch (_) {}
        try {
          const sid = (document.cookie.match(/(?:^|; )ab_session_id=([^;]*)/) || [])[1] || null;
          fetch('https://ab-test-node-5f422db672ba.herokuapp.com/api/v1/contact-form', {
            method: 'POST',
            keepalive: true,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              form_id: 'easy-pay-popup ' + window.location.pathname,
              submitted_data: { 'contact[email]': email },
              form_type: 'popup',
              sessionId: sid ? decodeURIComponent(sid) : null,
              listId: 'UfbrBe',
            }),
          }).catch(() => {});
        } catch (_) { /* lead capture must never break checkout */ }
        this._runAction(btn);
      }

      /** The configured post-CTA action: checkout (default) buys the selected
       *  bundle; 'bundles' closes the popup and scrolls to the selection.
       *  Fail-open: unknown/missing value behaves as checkout. */
      _runAction(btn) {
        if (this.getAttribute('data-ep-popup-action') === 'bundles') {
          this.close();
          const bundles = document.getElementById('ep-bundles');
          if (bundles) bundles.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        this._watch = buySelected(btn);
      }
    }
    customElements.define('ep-lp-popup', EpLpPopup);
  }
})();
